<template>
  <div
    ref="inviewRef"
    :class="[
      'relative bg-surface-light border border-surface-grey rounded-md transition duration-500 delay-400 has-[.calendar-open]:z-20', // overflow-hidden removed, is that ok?
      inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
    ]"
  >
    <div class="flex">
      <div
        class="w-30 md:w-45 shrink-0 aspect-square border-r border-surface-grey"
      >
        <img
          :src="photoToUrl(ticket.upsale_photo)"
          :alt="ticket.upsale_title"
          class="w-full h-1/2 md:h-full object-cover rounded-tl-md md:rounded-l-md"
        />
      </div>
      <div class="grow min-w-0 space-y-4 p-4 md:p-6">
        <div>
          <div class="h4 mb-1 truncate">
            {{ ticket.upsale_title }}
          </div>
          <div
            v-if="ticket.upsale_description"
            class="text-text text-sm break-words"
          >
            <div class="line-clamp-2">
              {{ plainTextDescription }}
            </div>
            <button
              type="button"
              class="underline cursor-pointer"
              @click="descriptionModal = true"
            >
              {{ $t('common.show_more') }}
            </button>
          </div>
        </div>

        <div class="flex flex-col md:flex-row justify-between md:items-center">
          <div class="mb-2 md:mb-0">
            <div class="flex items-baseline space-x-2">
              <div class="heavy">
                <span v-if="showFromPrefix" class="text-sm font-normal mr-1">{{
                  $t('common.from')
                }}</span
                >{{ formatPrice(displayPrice, event.currency, $i18n.locale) }}
              </div>

              <!-- <div TODO SSO prices
                v-if="ticket.beforePrice"
                class="text-text line-through text-sm"
              >
                {{ $t('common.before') }}
                {{
                  formatPrice(ticket.beforePrice, ticket.currency, $i18n.locale)
                }}
              </div> -->
            </div>
            <div
              class="text-text text-sm"
              v-if="actualTicketFee && actualTicketFee > 0"
            >
              + {{ formatPrice(actualTicketFee, event.currency, $i18n.locale) }}
              {{ $t('common.fee') }}
            </div>
          </div>

          <InputCount
            class="w-34"
            :model-value="cart.currentCount[ticket.id]"
            @update:model-value="onCountChange"
            :step="ticket.buy_amount_increment"
            :max="ticket.maxtickets"
            :first-step="ticket.mintickets"
            required
          />
        </div>
      </div>
    </div>

    <BaseSlideTransition>
      <div v-if="hasTicketCustomFields && cart.currentCount[ticket.id] > 0">
        <div
          class="py-4 px-6 border-t border-surface-grey bg-surface-grey-light font-semibold break-words"
        >
          {{ $t('checkout.add_info_to', { name: ticket.upsale_title }) }}
        </div>
        <CheckoutCustomFields
          type="upsaleTicket"
          :ticket-id="ticket.id"
          :event-id="eventID"
          :filters="['per_ticket', 'per_tickettype']"
          :errors="undefined"
        />
      </div>
    </BaseSlideTransition>

    <BaseModal v-model="descriptionModal">
      <template #header>{{ ticket.upsale_title }}</template>
      <div class="p-6 text-sm break-words" v-html="ticket.upsale_description" />
    </BaseModal>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';

  import { useCartStore } from '@/stores/cart';
  import type { EventCheckoutData, TicketCheckoutData } from '@/types';
  import { formatPrice } from '@/utils/formatPrice';
  import { htmlToPlainText } from '@/utils/htmlToPlainText';
  import { photoToUrl } from '@/utils/photoToUrl';
  import { useInView } from '@/utils/useInView';

  import BaseModal from '../ui/BaseModal.vue';
  import BaseSlideTransition from '../ui/BaseSlideTransition.vue';
  import InputCount from '../ui/inputs/InputCount.vue';
  import CheckoutCustomFields from './CheckoutCustomFields.vue';

  const props = defineProps<{
    ticket: TicketCheckoutData;
    event: EventCheckoutData;
    eventID: number | string | undefined;
  }>();

  const cart = useCartStore();

  const cartEvent = cart.items.find(item => item.id === props.eventID);

  console.log('CheckoutTicket - cart items:', cart.items);
  console.log(
    'CheckoutTicket - looking for event ID',
    props.eventID,
    'found:',
    cartEvent
  );
  console.log('Event data for event ID', props.eventID, ':', props.event);
  // console.log('CheckoutTicket - cartEvent for event ID', props.eventID, ':', cartEvent);
  const ticketCount =
    cartEvent?.tickets.find(t => t.id == props.ticket.id)?.count || 0;

  cart.currentCount[props.ticket.id] = ticketCount;

  function onCountChange(newCount: number) {
    cart.currentCount[props.ticket.id] = newCount;
    cart.queueTicketChange(props.eventID, props.ticket.id, newCount);
  }
  // console.log('CheckoutTicket - ticket count for ticket ID', props.ticket.id, 'is', cart.currentCount[props.ticket.id]);

  const hasTicketCustomFields = computed(() => {
    if (!cart.currentOrderData.customfields_data || !props.eventID)
      return false;
    const eventFields = cart.currentOrderData.customfields_data[props.eventID];
    if (!eventFields) return false;
    return Object.values(eventFields).some(
      field =>
        field.ticketID === props.ticket.id &&
        ['per_ticket', 'per_tickettype'].includes(field.type)
    );
  });

  const hasPriceGroups = computed(
    () => (props.ticket.price_groups?.length ?? 0) > 0
  );
  const confirmedTotal = computed(
    () => cart.confirmedSeatPrices[props.ticket.id] ?? null
  );
  const ticketCountInCart = computed(
    () => cart.currentCount[props.ticket.id] || 1
  );
  const displayPrice = computed(() => {
    if (hasPriceGroups.value) {
      if (confirmedTotal.value != null && ticketCountInCart.value > 0) {
        return Math.round(confirmedTotal.value / ticketCountInCart.value);
      }
      return props.ticket.min_price;
    }
    return props.ticket.price;
  });
  const showFromPrefix = computed(
    () => hasPriceGroups.value && confirmedTotal.value == null
  );

  const actualTicketFee = computed(() => {
    return (
      props.ticket.ticketfee + displayPrice.value * props.ticket.ticketfee_extra
    );
  });

  const { inviewRef, inView } = useInView();

  const descriptionModal = ref(false);

  const plainTextDescription = computed(() =>
    htmlToPlainText(props.ticket.upsale_description || '')
  );
</script>
